import { apiClient } from './client';
import type {
  TicketApi, TicketSummary, TicketDetail, CreateTicketInput,
} from '@/components/tickets/types';

interface Envelope<T> { success: boolean; data: T; meta?: { unread?: number } }

/**
 * Build a persona-scoped ticket API from a route prefix ('agent' | 'admin' | 'super').
 * Reused by all three persona Tickets pages so the UI logic lives once.
 */
export function makeTicketApi(prefix: 'agent' | 'admin' | 'super'): TicketApi {
  const base = `/${prefix}/tickets`;

  function form(input: { body?: string; subject?: string; audience?: string; priority?: string; status?: string; files?: File[] }): FormData {
    const fd = new FormData();
    if (input.subject !== undefined) fd.append('subject', input.subject);
    if (input.body !== undefined) fd.append('body', input.body);
    if (input.audience !== undefined) fd.append('audience', input.audience);
    if (input.priority !== undefined) fd.append('priority', input.priority);
    if (input.status !== undefined) fd.append('status', input.status);
    (input.files ?? []).forEach((f) => fd.append('files[]', f));
    return fd;
  }

  return {
    async list(status?: string) {
      const { data } = await apiClient.get<Envelope<TicketSummary[]>>(base, { params: { status } });
      return { data: data.data ?? [], unread: data.meta?.unread ?? 0 };
    },
    async get(id: number) {
      const { data } = await apiClient.get<Envelope<TicketDetail>>(`${base}/${id}`);
      return data.data;
    },
    async create(input: CreateTicketInput) {
      const { data } = await apiClient.post<Envelope<TicketDetail>>(base, form(input));
      return data.data;
    },
    async reply(id: number, body: string, files?: File[], status?: string) {
      const { data } = await apiClient.post<Envelope<TicketDetail>>(`${base}/${id}/messages`, form({ body, files, status }));
      return data.data;
    },
    async update(id: number, patch: { status?: string; priority?: string }) {
      const { data } = await apiClient.patch<Envelope<TicketSummary>>(`${base}/${id}`, patch);
      return data.data;
    },
    async markRead(id: number) {
      await apiClient.post(`${base}/${id}/read`);
    },
    attachmentUrl(attachmentId: number) {
      const root = (apiClient.defaults.baseURL ?? '/api/v1/merkaziya').replace(/\/$/, '');
      return `${root}${base}/attachments/${attachmentId}`;
    },
  };
}
